/**
 * Modal for creating a new DocumentTemplate at the current scope.
 *
 * Two ways in:
 * - "Start from scratch": name / key / description, created immediately.
 * - "Upload a document" (office scope only): a PDF or DOCX file is turned into a
 *   *draft* template by an LLM (sections, writing instructions, and interview
 *   questions matched to existing specs where possible). The draft opens in the
 *   editor for review, and a person publishes it.
 *
 * "From an existing template" was deliberately deferred — upstream templates
 * auto-populate in the Manager list already, so there's no need to copy them.
 */
import { useEffect, useState } from 'react'
import Modal from '../ui/Modal'
import FileDropzone from '../ui/FileDropzone'
import JobProgressCard from '../jobs/JobProgressCard'
import type { Job } from '../../hooks/useJobPoll'
import { createTemplate, getTemplate, importTemplate, waitForTemplateImport } from './api'
import type { DocumentTemplate, Scope } from './types'

interface Props {
  isOpen: boolean
  onClose: () => void
  scope: Scope
  onCreated: (tpl: DocumentTemplate) => void
}

type Mode = 'scratch' | 'upload'

const UPLOAD_ACCEPT = '.pdf,.docx'

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80)
}

export default function NewTemplateModal({ isOpen, onClose, scope, onCreated }: Props) {
  const [mode, setMode] = useState<Mode>('scratch')
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [job, setJob] = useState<Job | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canUpload = scope.level === 'office' && !!scope.ownerId

  useEffect(() => {
    if (isOpen) {
      setMode('scratch')
      setName('')
      setKey('')
      setKeyTouched(false)
      setDescription('')
      setFile(null)
      setJob(null)
      setError(null)
    }
  }, [isOpen])

  // Auto-generate key from name until the user edits the key manually.
  useEffect(() => {
    if (!keyTouched) setKey(slugify(name))
  }, [name, keyTouched])

  const handleCreate = async () => {
    if (!name.trim() || !key.trim()) {
      setError('Name and key are both required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const tpl = await createTemplate({
        key: key.trim(),
        name: name.trim(),
        owner_level: scope.level,
        owner_office: scope.level === 'office' ? (scope.ownerId || null) : null,
        owner_user: scope.level === 'program_manager' ? (scope.ownerId || null) : null,
        owner_program: scope.level === 'program' ? (scope.ownerId || null) : null,
        parent: null,
        description: description || null,
        writing_agent_instructions: null,
        version: 1,
        is_active: true,
      } as any)
      onCreated(tpl)
    } catch (err: any) {
      setError(err.message || 'Failed to create template')
    } finally {
      setSaving(false)
    }
  }

  const handleImport = async () => {
    if (!file || !scope.ownerId) return
    setSaving(true)
    setError(null)
    try {
      const { job_id } = await importTemplate(file, scope.ownerId)
      const templateId = await waitForTemplateImport(job_id, { onUpdate: setJob })
      onCreated(await getTemplate(templateId))
    } catch (err: any) {
      setError(err.message || 'Failed to import template')
    } finally {
      setSaving(false)
      setJob(null)
    }
  }

  const isUpload = mode === 'upload'
  const primaryDisabled = saving || (isUpload ? !file : !name.trim() || !key.trim())
  const primaryLabel = isUpload
    ? (saving ? 'Reading document…' : 'Import')
    : (saving ? 'Creating…' : 'Create')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New document template"
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-border rounded-md"
            disabled={saving && !isUpload}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isUpload ? handleImport : handleCreate}
            disabled={primaryDisabled}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50"
          >
            {primaryLabel}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {canUpload && (
          <div className="inline-flex rounded-md border border-border p-0.5 text-sm" role="tablist">
            {(['scratch', 'upload'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                onClick={() => { setMode(m); setError(null) }}
                disabled={saving}
                className={`px-3 py-1.5 rounded ${mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {m === 'scratch' ? 'Start from scratch' : 'Upload a document'}
              </button>
            ))}
          </div>
        )}

        {isUpload ? (
          <>
            <p className="text-sm text-muted-foreground">
              Upload an existing template or example document (PDF or DOCX). Its sections, writing
              guidance and key interview questions are extracted into a draft you can review and edit
              before publishing. Questions reuse existing specs where one fits.
            </p>
            {saving ? (
              job ? <JobProgressCard job={job} /> : (
                <div className="text-sm text-muted-foreground">Uploading {file?.name}…</div>
              )
            ) : (
              <FileDropzone
                accept={UPLOAD_ACCEPT}
                multiple={false}
                label={file ? file.name : 'Drop a PDF or DOCX here, or click to browse'}
                hint={file ? 'Click or drop to choose a different file' : 'PDF or DOCX, up to 16 MB'}
                onFilesSelected={(files) => { setFile(files[0] ?? null); setError(null) }}
              />
            )}
            {saving && (
              <p className="text-xs text-muted-foreground">
                Large documents can take a few minutes. You can close this dialog; the draft
                will appear in your list when it's ready.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Start a new document type from scratch at the {scope.level.replace('_', ' ')} scope.
              Upstream templates already appear in your list automatically.
            </p>

            <label className="block">
              <span className="text-sm font-medium">Name</span>
              <input
                autoFocus
                type="text"
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Acquisition Strategy"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium">Key</span>
              <input
                type="text"
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground font-mono text-sm"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value)
                  setKeyTouched(true)
                }}
                placeholder="acquisition_strategy"
              />
              <span className="text-xs text-muted-foreground mt-1 block">
                Machine identifier. Auto-generated from the name; edit if needed. Must be unique within this scope.
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium">Description (optional)</span>
              <textarea
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this document for? When is it used?"
              />
            </label>
          </>
        )}

        {error && <div className="text-sm text-destructive">{error}</div>}
      </div>
    </Modal>
  )
}
